import { useUserMe } from "~/components/user/composables/useUser";
import { useApi } from "~/composables/useApi";
import { useSupabase } from "~/composables/useSupabase";
import type { BusinessUnit } from "@asap/shared/schemas/business-unit";
import type { Department } from "@asap/shared/schemas/department";
import type { MissionSchema } from "@asap/shared/schemas/mission/list";
import type { MissionTableQueryParams } from "@asap/shared/schemas/mission/mission";
import type { Sorting } from "@asap/shared/types/sorting";
import type { MissionClosingReason } from "@asap/shared/types/supabase.types.gen";
import { getBounds } from "@asap/shared/utils/geography";
import { missionForRowColumns } from "@asap/shared/utils/supabase.columns";
import type { JobPostingStatus, MissionStatus } from "@asap/shared/utils/supabase.enum";
import type { Address, Company, MissionForRow, Region, TeamMemberForAvatar } from "@asap/shared/utils/supabase.types";
import type { SortingState } from "@tanstack/vue-table";
import Fuse from "fuse.js";
import { chunk } from "lodash-es";
import { useWorkspaceFilter, useWorkspaceMe } from "~/components/workspace/composables/useWorkspace";

type UseMissionsFilterParams = {
  coordinates: Ref<Address["coordinates"] | undefined>;
  searchRadius: Ref<number>;
  businessUnits: Ref<BusinessUnit[]>;
  jobPostingStatus: Ref<JobPostingStatus[]>;
  companies: Ref<Company[]>;
  teamMembers: Ref<TeamMemberForAvatar[]>;
  statuses: Ref<MissionStatus[]>;
  priority: Ref<number[]>;
  departments: Ref<Department[]>;
  regions: Ref<Region[]>;
  isClientOrder: Ref<boolean>;
  closingReasons: Ref<MissionClosingReason[]>;
};

export const useMissionsFiltered = function (missions: Ref<MissionForRow[]>, params: UseMissionsFilterParams) {
  const {
    coordinates,
    searchRadius,
    jobPostingStatus,
    businessUnits,
    teamMembers,
    companies,
    statuses,
    priority,
    departments,
    regions,
    isClientOrder,
    closingReasons,
  } = params;

  const loading = ref(true);

  const { workspaceId, isIndependentAdministrator, isSupportRole, mainWorkspace } = useUserMe();
  const { workspaceType } = useWorkspaceFilter();
  const supabase = useSupabase();
  const { canSwitchWorkspace } = useWorkspaceMe();

  watchEffect(async (onCleanup) => {
    if (!workspaceId.value) return;

    const abortController = new AbortController();

    onCleanup(() => {
      abortController.abort();
    });

    loading.value = true;

    const defaultSelectColumns = `
      id,
      address_table(
        department(
          code,
          region_code
        )
      )
    `;

    let selectColumns = defaultSelectColumns;

    if (businessUnits.value.length) {
      selectColumns = selectColumns.replace("business_units:business_unit", "business_units:business_unit!inner");
    }

    if (coordinates.value || departments.value.length || regions.value.length) {
      selectColumns = selectColumns
        .replace("address_table", "address_table!inner")
        .replace("department", "department!inner");
    }

    const query = supabase
      .from("mission")
      .select(selectColumns as typeof defaultSelectColumns)
      .abortSignal(abortController.signal);

    if (isClientOrder.value) query.eq("is_client_order", true);
    else query.eq("is_client_order", false);

    if (closingReasons.value.length) {
      query.in("closing_reason", closingReasons.value);
    }

    if (canSwitchWorkspace.value) {
      if (workspaceType.value === "internal") query.eq("workspace_id", workspaceId.value);
      if (workspaceType.value === "external") query.neq("workspace_id", workspaceId.value);
    }

    if (!isSupportRole.value && !isIndependentAdministrator && mainWorkspace.value) {
      query.eq("mission_workspaces.workspace_id", mainWorkspace.value.id);
    }

    if (coordinates.value && coordinates.value.lng && coordinates.value.lat && searchRadius.value) {
      const { minLng, maxLng, minLat, maxLat } = getBounds(
        {
          lng: coordinates.value.lng,
          lat: coordinates.value.lat,
        },
        searchRadius.value
      );

      query
        .gte("address_table.longitude", minLng)
        .lte("address_table.longitude", maxLng)
        .gte("address_table.latitude", minLat)
        .lte("address_table.latitude", maxLat);
    }

    if (departments.value.length) {
      query.in(
        "address_table.department_code",
        departments.value.map((department) => department.code)
      );
    }

    if (regions.value.length) {
      query.in(
        "address_table.department.region_code",
        regions.value.map((region) => region.code)
      );
    }

    if (businessUnits.value.length) {
      query.in(
        "business_unit_id",
        businessUnits.value.map(({ id }) => id)
      );
    }

    if (companies.value.length) {
      query.in(
        "company_id",
        companies.value.map(({ id }) => id)
      );
    }

    if (teamMembers.value.length) {
      const teamMemberIds = teamMembers.value.map(({ id }) => id).join(",");
      const teamMemberAuthUserIds = teamMembers.value.map(({ auth_user_id }) => auth_user_id).join(",");
      const orCondition = `owner_id.in.(${teamMemberIds}), assignee_id.in.(${teamMemberIds}), created_by.in.(${teamMemberAuthUserIds})`;
      query.or(orCondition);
    }

    if (jobPostingStatus.value.length) {
      query.in("job_posting_status", jobPostingStatus.value);

      if (jobPostingStatus.value.includes("published")) {
        query.not("job_posting_date", "is", null).order("job_posting_date", { ascending: false });
      }
    }

    if (statuses.value.length) {
      query.in("status", statuses.value);
    }

    if (priority.value.length) {
      query.in("priority", priority.value);
    }

    const { data, error } = await query.order("priority").order("created_at", { ascending: false });

    if (isAbortError(error)) return;
    if (error) console.error({ error });
    if (error) throw error;

    const missionIds = data.map((mission) => mission.id);
    const chunkSize = 100;
    const missionIdsChunks = chunk(missionIds, chunkSize);

    const numberOfParallelRequests = 10;
    const batches = chunk(missionIdsChunks, numberOfParallelRequests);

    const completeMissions: MissionForRow[] = [];

    for (const batch of batches) {
      const arrayOfMissions = await Promise.all(
        batch.map(async (missionIds) => {
          const { data: completeMissions, error: completeMissionsError } = await supabase
            .from("mission")
            .select<string, MissionForRow>(missionForRowColumns)
            .in("id", missionIds)
            .order("updated_at", { ascending: false })
            .abortSignal(abortController.signal);

          if (isAbortError(completeMissionsError)) return [];
          if (completeMissionsError) throw completeMissionsError;

          return completeMissions;
        })
      );

      completeMissions.push(...arrayOfMissions.flat());
    }

    missions.value = completeMissions;

    loading.value = false;
  });

  return { loading };
};

export const useMissionsLocalSearch = function (missions: Ref<MissionForRow[]>, searchTerm: Ref<string>) {
  const searchFilter = function (missions: MissionForRow[], searchTerm: string) {
    if (!searchTerm) return missions;

    const fuse = new Fuse(missions, {
      keys: ["job_name", "company.name"],
      threshold: 0.3,
    });

    return fuse.search(searchTerm).map((result) => result.item);
  };

  return computed(() => searchFilter(missions.value, searchTerm.value));
};

export const useMissionsTable = function () {
  const { invoke } = useApi();

  const getMissionsTableRows = async (
    params: MissionTableQueryParams,
    offset?: number,
    sorting?: Sorting,
    signal?: AbortSignal
  ) => {
    const response = await invoke({
      path: "mission/table",
      method: "GET",
      query: {
        ...(offset ? { offset } : {}),
        ...(sorting ? { ...sorting } : {}),
        ...params,
      },
      signal,
    });

    if (!response.success) {
      if (isAbortError(response.error)) return [];
      throw new Error(response.error.message);
    }

    return response.data;
  };

  return { getMissionsTableRows };
};

type MissionFilters = {
  pageIndex: number;
  pageSize: number;
  search?: string;
  companyId?: string;
  contactId?: string;
  status?: MissionStatus;
  businessUnitIds?: string[];
  teamMemberIds?: string[];
  orders: { column: string; direction: "asc" | "desc" }[];
};

type MissionState = {
  selectedFilters: MissionFilters;
  totalItems: number;
  loading: boolean;
  sorting: SortingState;
};

const missionState = reactive<MissionState>({
  selectedFilters: {
    pageSize: 20,
    pageIndex: 0,
    orders: [],
    businessUnitIds: undefined,
  },
  totalItems: 0,
  loading: false,
  sorting: [{ id: "created_at", desc: true }],
});

export const useMissions = function () {
  const { invoke } = useApi();

  const convertSortingToOrders = (sorting: SortingState): { column: string; direction: "asc" | "desc" }[] => {
    return sorting.map((sort) => ({
      column: sort.id,
      direction: sort.desc ? "desc" : "asc",
    }));
  };

  const selectMultipleForTable = async function (
    filters: MissionFilters
  ): Promise<{ data: MissionSchema[]; count: number }> {
    missionState.loading = true;
    const order = filters.orders.at(0);

    const response = await invoke({
      path: "missions",
      method: "GET",
      query: {
        pageIndex: filters.pageIndex,
        pageSize: filters.pageSize,
        search: filters.search,
        orderColumn: order?.column,
        orderDirection: order?.direction,
        companyId: filters.companyId,
        contactId: filters.contactId,
        status: filters.status,
        businessUnitIds: filters.businessUnitIds,
        teamMemberIds: filters.teamMemberIds,
      },
    });

    missionState.loading = false;

    if (!response.success) {
      throw new Error(response.error.message);
    }

    return { data: response.data.items, count: response.data.totalItems };
  };

  return {
    missionState,
    selectMultipleForTable,
    convertSortingToOrders,
  };
};
